import { TimeInterval } from '@Common/Enums';
import { CheckParameter, FilterParameter, ListValueParameter, ValueParameter } from './FilterParameters';
import timeHelper from '@Common/Helpers/TimeHelper';
import { IComparer } from './FilterManager';
import { comparers } from '@Common/Helpers/Comparers';

export class FilterOperation {
    constructor(private _description: string) {}

    public getParameters(): FilterParameter[] {
        return _(this as any)
            .filter(value => value instanceof FilterParameter)
            .value();
    }

    public description(desc?: string): string {
        if (arguments.length === 0) {
            return this._description;
        } else {
            return (this._description = desc);
        }
    }

    public values(values?: any): any {
        const parameters = this.getParameters();
        if (arguments.length === 0) {
            return parameters.searchTree(
                p => p.getName(),
                p => p.value()
            );
        }

        for (const p of parameters) {
            p.value(values[p.getName()]);
        }
        return values;
    }

    public isRequiredValues(): boolean {
        return this.getParameters().some(p => p.initValue() == null);
    }

    public execute(compare?: IComparer): FilterExecutor {
        return this.getFilterExecutor(this.values(), compare);
    }

    public getFilterExecutor(values: any, compare?: IComparer): FilterExecutor {
        return new FilterExecutor(this, compare);
    }

    public equals(filterOperation: FilterOperation): boolean {
        if (this.constructor !== filterOperation.constructor) {
            return false;
        }
        const parameters = filterOperation.getParameters();
        const parameters1 = this.getParameters();
        const equals = _.zipWith(parameters, parameters1, (p1, p2) => p1.value() === p2.value());
        return _.every(equals);
    }

    public initValues(values: any): void {
        for (const p of this.getParameters()) {
            p.initValue(values[p.getName()]);
        }
    }
}

export class FilterExecutor {
    protected comparer: IComparer;
    protected operation: FilterOperation;

    constructor(operation: FilterOperation, compare?: IComparer) {
        this.operation = operation;
        this.comparer = compare || comparers.comparer;
    }

    public filter(value: any): boolean {
        return true;
    }
}

export class NullFilterExecutor extends FilterExecutor {
    constructor(operation?: FilterOperation) {
        super(operation);
    }
}

export class NopFilterOperation extends FilterOperation {
    constructor() {
        super(strings.simpleGridLight.filters.none);
    }

    public getFilterExecutor(values?: any): FilterExecutor {
        return new NullFilterExecutor(this);
    }
}

class SimpleCompareExecutor extends FilterExecutor {
    constructor(operation: FilterOperation, private compareValue: string, private possibleCompareValues: number[]) {
        super(operation);
    }

    public filter(value: any): boolean {
        if (this.compareValue === undefined) {
            return true;
        }
        const compare = this.comparer(value, this.compareValue);
        return this.possibleCompareValues.contains(compare);
    }
}

export class SimpleCompareFilterOperation extends FilterOperation {
    public compareValue: ListValueParameter = new ListValueParameter(strings.simpleGridLight.filters.value, 'compareValue');

    constructor(description: string, private comparerRes: number[]) {
        super(description);
    }

    public getFilterExecutor(values?: any, compare?: IComparer): FilterExecutor {
        return new SimpleCompareExecutor(this, values.compareValue, this.comparerRes);
    }
}

export class StringFilterOperation extends FilterOperation {
    public match: ValueParameter = new ValueParameter(strings.simpleGridLight.filters.match, 'match');

    constructor(desc: string) {
        super(desc);
    }
}

class StringFilterExecutor extends FilterExecutor {
    constructor(operation: FilterOperation, private match: string, private testFn: (value: string, match: string) => boolean) {
        super(operation);
    }

    public filter(value: any): boolean {
        if (!this.match) {
            return true;
        }
        return value && this.testFn(value, this.match);
    }
}

export class StringContainsFilterOperation extends StringFilterOperation {
    constructor() {
        super(strings.simpleGridLight.filters.contains);
    }

    public getFilterExecutor(values?: any, compare?: IComparer): FilterExecutor {
        return new StringFilterExecutor(this, values.match, (s1, s2) => s1.toLowerCase().indexOf(s2.toLowerCase()) >= 0);
    }
}

class ArrayItemFilterExecutor extends FilterExecutor {
    constructor(operation: FilterOperation, private match: string, private testFn: (value: string, match: string) => boolean) {
        super(operation);
    }

    public filter(value: any): boolean {
        if (!this.match) {
            return true;
        }
        return _(value).some(i => this.testFn(i, this.match));
    }
}

export class ArrayItemContainsStringFilterOperation extends StringFilterOperation {
    constructor() {
        super(strings.simpleGridLight.filters.contains);
    }

    public getFilterExecutor(values?: any, compare?: IComparer): FilterExecutor {
        return new ArrayItemFilterExecutor(this, values.match, (s1, s2) => s1.toLowerCase().indexOf(s2.toLowerCase()) >= 0);
    }
}

export class BetweenFilterOperation extends FilterOperation {
    public start: ListValueParameter = new ListValueParameter(strings.simpleGridLight.filters.start, 'start');
    public end: ListValueParameter = new ListValueParameter(strings.simpleGridLight.filters.end, 'end');
    public isInclusive: CheckParameter = new CheckParameter(strings.simpleGridLight.filters.isInclusive, 'isInclusive');

    constructor() {
        super(strings.simpleGridLight.filters.between);
    }

    public getFilterExecutor(values?: any, compare?: IComparer): FilterExecutor {
        return new BetweenFilterExecuter(this, compare, values.start, values.end, values.isInclusive);
    }
}

export class BetweenFilterExecuter extends FilterExecutor {
    constructor(operation: FilterOperation, compare: IComparer, private start: any, private end: any, private isInclusive: boolean) {
        super(operation, compare);
    }

    public filter(value: any): boolean {
        const compareStart = this.comparer(value, this.start);
        const compareEnd = this.comparer(value, this.end);
        const boundary = this.isInclusive ? 1 : 0;
        return -boundary < compareStart && compareEnd < boundary;
    }
}

export class DateFilterOperation extends FilterOperation {
    public date: ListValueParameter = new ListValueParameter(strings.simpleGridLight.filters.date, 'date');

    constructor() {
        super('DateFilter');
    }
}

export class DateInterval {
    constructor(public from: Date, public to: Date) {}
}

export class DateIntervals {
    public static hour = 'hours';
    public static day = 'days';
    public static week = 'weeks';
    public static month = 'months';

    public static getInterval(date: Date, interval: string, shift: DateInervalShift): DateInterval {
        const thi = DateIntervals.getTimeHelperInterval(interval);
        const intervalStartTime = timeHelper.nearestStartTime(date, thi);
        const from = intervalStartTime.getTime() + timeHelper.getAlignedIntervalTicks(thi, shift, intervalStartTime);
        const to = intervalStartTime.getTime() + timeHelper.getAlignedIntervalTicks(thi, shift + 1, intervalStartTime);
        return new DateInterval(new Date(from), new Date(to));
    }

    private static getTimeHelperInterval(interval: string): TimeInterval {
        switch (interval) {
            case DateIntervals.hour:
                return TimeInterval.hour1;
            case DateIntervals.day:
                return TimeInterval.day1;
            case DateIntervals.week:
                return TimeInterval.week1;
            case DateIntervals.month:
                return TimeInterval.month1;
            default:
                throw new Error("Invalid '{0}' interval kind".format(interval));
        }
    }
}

export enum DateInervalShift {
    lastInterval = -1,
    thisInterval = 0,
    nextInterval = 1
}

export class DateInIntervalFilterOperation extends FilterOperation {
    constructor(private intraval: string, private shift: DateInervalShift) {
        super('DateFilter');
    }

    public getFilterExecutor(values?: any, compare?: IComparer): FilterExecutor {
        const now = new Date();
        const interval = DateIntervals.getInterval(now, this.intraval, this.shift);
        return new BetweenFilterExecuter(this, compare, interval.from, interval.to, true);
    }
}

export class DateFilterExecutor extends FilterExecutor {
    constructor(operation: FilterOperation, compare: IComparer, protected value: Date) {
        super(operation, compare);
    }
}

export class InFilterOperation extends FilterOperation {
    constructor(defaults: string[]) {
        super(strings.simpleGridLight.filters.inFilter);
        this.selectedValues = defaults;
    }

    public getFilterExecutor(values?: any, compare?: IComparer): FilterExecutor {
        return new InFilterExecutor(this, compare, this.selectedValues);
    }

    public selectedValues: any[] = [];
}

class InFilterExecutor extends FilterExecutor {
    constructor(operation: FilterOperation, compare: IComparer, protected values: any[]) {
        super(operation, compare);
    }

    public filter(value: any): boolean {
        return this.values.length === 0 || this.values.some(v => this.comparer(v, value) === 0);
    }
}

export class InArrayFilterOperation extends FilterOperation {
    constructor() {
        super(strings.simpleGridLight.filters.inFilter);
    }

    public getFilterExecutor(values?: any, compare?: IComparer): FilterExecutor {
        return new InArrayFilterExecutor(this, compare, this.selectedValues);
    }

    public selectedValues: any[] = [];
}

class InArrayFilterExecutor extends FilterExecutor {
    constructor(operation: FilterOperation, compare: IComparer, protected values: any[]) {
        super(operation, compare);
    }

    public filter(value: any[]): boolean {
        return (
            this.values.length === 0 || this.values.some(v => (value.length === 0 && v.length === 0) || value.some(v2 => this.comparer(v, v2) === 0))
        );
    }
}
